// ============================================================
// useMoveSwap — 予約移動・入替・車両スワップロジック
// ============================================================

import { useState, useCallback, useRef } from 'react';
import { Reservation, Vehicle } from './types';
import { saveDispatchReservation } from '../../api/dispatchService';
import { toScheduleOnlyPayload } from './schedulePayload';
import { saveScheduleChange } from './saveScheduleChange';
import { buildMemoEntry, appendMemoEntry } from './memoLog';
import { beginOptimistic, settleOptimistic, rollbackOptimistic } from './optimisticCache';
import { StagingController } from './useStaging';

function formatDateShort(d: Date): string {
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export type InteractionMode = 'none' | 'move' | 'move-free' | 'swap' | 'vehicle-swap';

interface UndoAction {
  label: string;
  execute: () => Promise<void>;
}

const WARNING_KEYWORDS = ['車検', '点検', '修理', 'タイヤ', '指定'];

export interface VehicleSwapInfo {
  vehicleA: Vehicle;
  vehicleB: Vehicle;
  /** 入替対象の予約（未来 + 進行中でない） */
  swapTargetsA: Reservation[];
  swapTargetsB: Reservation[];
  /** 警告予約（キーワードに一致する入替対象） */
  warningsA: Reservation[];
  warningsB: Reservation[];
  /** 除外される予約（過去 or 進行中 in_use/done） */
  excludedA: Reservation[];
  excludedB: Reservation[];
}

interface UseMoveSwapProps {
  reservations: Reservation[];
  vehicles: Vehicle[];
  onComplete: () => void;
  onToast?: (message: string, severity: 'success' | 'error') => void;
  /** ステージングモード: active 中は保存せず stage() に積む */
  staging?: StagingController;
}

interface UseMoveSwapReturn {
  mode: InteractionMode;
  selectedReservation: Reservation | null;
  enterMove: () => void;
  enterSwap: () => void;
  enterVehicleSwap: () => void;
  startMove: (r: Reservation, freeMove?: boolean) => void;
  startSwap: (r: Reservation) => void;
  executeMove: (targetVehicleId: string, targetDate: Date) => Promise<void>;
  executeSwap: (targetReservation: Reservation) => Promise<void>;
  cancel: () => void;
  undoAction: UndoAction | null;
  dismissUndo: () => void;
  // vehicle-swap
  vehicleSwapSource: Vehicle | null;
  vehicleSwapInfo: VehicleSwapInfo | null;
  selectVehicleSwapSource: (v: Vehicle) => void;
  selectVehicleSwapTarget: (v: Vehicle) => void;
  executeVehicleSwap: () => Promise<void>;
  cancelVehicleSwap: () => void;
}

export function useMoveSwap({ reservations, vehicles, onComplete, onToast, staging }: UseMoveSwapProps): UseMoveSwapReturn {
  const [mode, setMode] = useState<InteractionMode>('none');
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  const [undoAction, setUndoAction] = useState<UndoAction | null>(null);
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // vehicle-swap state
  const [vehicleSwapSource, setVehicleSwapSource] = useState<Vehicle | null>(null);
  const [vehicleSwapInfo, setVehicleSwapInfo] = useState<VehicleSwapInfo | null>(null);

  const scheduleUndoDismiss = useCallback((action: UndoAction) => {
    setUndoAction(action);
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    undoTimerRef.current = setTimeout(() => setUndoAction(null), 5000);
  }, []);

  const enterMove = useCallback(() => {
    setMode('move');
    setSelectedReservation(null);
  }, []);

  const enterSwap = useCallback(() => {
    setMode('swap');
    setSelectedReservation(null);
  }, []);

  const enterVehicleSwap = useCallback(() => {
    setMode('vehicle-swap');
    setSelectedReservation(null);
    setVehicleSwapSource(null);
    setVehicleSwapInfo(null);
  }, []);

  const startMove = useCallback((r: Reservation, freeMove?: boolean) => {
    setMode(freeMove ? 'move-free' : 'move');
    setSelectedReservation(r);
  }, []);

  const startSwap = useCallback((r: Reservation) => {
    setMode('swap');
    setSelectedReservation(r);
  }, []);

  const cancel = useCallback(() => {
    setMode('none');
    setSelectedReservation(null);
    setVehicleSwapSource(null);
    setVehicleSwapInfo(null);
  }, []);

  const dismissUndo = useCallback(() => {
    setUndoAction(null);
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
  }, []);

  // ========== 予約移動 ==========
  const executeMove = useCallback(async (targetVehicleId: string, targetDate: Date) => {
    if (!selectedReservation) return;

    // 同じ車両 & 同じ日時への移動は no-op（スキップ）
    const sameVehicle = String(selectedReservation.vehicle_id) === String(targetVehicleId);
    if (mode !== 'move-free' && sameVehicle) {
      setMode('none');
      setSelectedReservation(null);
      return;
    }
    if (mode === 'move-free' && sameVehicle) {
      const origStart = new Date(selectedReservation.start_date);
      if (Math.abs(origStart.getTime() - targetDate.getTime()) < 60000) {
        setMode('none');
        setSelectedReservation(null);
        return;
      }
    }

    const original = { ...selectedReservation };
    let updated = { ...selectedReservation, vehicle_id: targetVehicleId };

    if (mode === 'move-free') {
      const origStart = new Date(selectedReservation.start_date);
      const origEnd = new Date(selectedReservation.end_date);
      const duration = origEnd.getTime() - origStart.getTime();
      updated.start_date = targetDate.toISOString();
      updated.end_date = new Date(targetDate.getTime() + duration).toISOString();
    }

    // ステージングモード: 保存せずローカルに積むだけ（キャッシュにも触れない）
    if (staging?.active) {
      staging.stage(
        original,
        mode === 'move-free'
          ? { vehicle_id: targetVehicleId, start_date: updated.start_date, end_date: updated.end_date }
          : { vehicle_id: targetVehicleId },
        mode === 'move-free' ? '予約移動（自由）' : '予約移動',
      );
      setMode('none');
      setSelectedReservation(null);
      return;
    }

    // メモ追記（楽観更新用。永続化は saveScheduleChange が fresh のメモに entry を追記する）
    const fromVehicle = vehicles.find(v => String(v.id) === String(original.vehicle_id));
    const toVehicle = vehicles.find(v => String(v.id) === targetVehicleId);
    const fromCode = fromVehicle?.vehicle_code || original.vehicle_id;
    const toCode = toVehicle?.vehicle_code || targetVehicleId;
    const memoEntry = mode === 'move-free'
      ? buildMemoEntry('予約移動（自由）', `${fromCode} → ${toCode}\n日時: ${formatDateShort(targetDate)}`)
      : buildMemoEntry('予約移動', `${fromCode} → ${toCode}`);
    updated = { ...updated, memo: appendMemoEntry(updated.memo, memoEntry) };

    beginOptimistic([updated]);
    setMode('none');
    setSelectedReservation(null);

    scheduleUndoDismiss({
      label: '移動を元に戻す',
      execute: async () => {
        beginOptimistic([original]);
        try {
          const undone = await saveDispatchReservation(toScheduleOnlyPayload(original));
          settleOptimistic([original.id], undone);
        } catch (e) {
          rollbackOptimistic([updated]);
          throw e;
        }
      },
    });

    try {
      const saved = await saveScheduleChange(updated, memoEntry);
      // サーバー確定値をキャッシュへ（Pusher エコー / ポーリングを待たない）。
      // これで updated_at がサーバー正本に進み、保存前スナップショットが
      // 後から届いても updateCache の比較で弾かれる。
      settleOptimistic([updated.id], saved);
      onToast?.('移動が完了しました', 'success');
    } catch (e) {
      rollbackOptimistic([original]);
      console.error('[useMoveSwap] move failed, reverted:', e);
      onToast?.('移動に失敗しました', 'error');
    }
  }, [selectedReservation, mode, scheduleUndoDismiss, onToast, vehicles, staging]);

  // ========== 予約入替（2予約の vehicle_id 交換） ==========
  const executeSwap = useCallback(async (targetReservation: Reservation) => {
    if (!selectedReservation) return;
    const r1 = selectedReservation;
    const r2 = targetReservation;
    if (r1.id === r2.id) { cancel(); return; }

    // ステージングモード: 両予約の vehicle_id 交換をローカルに積むだけ
    if (staging?.active) {
      staging.stage(r1, { vehicle_id: String(r2.vehicle_id) }, '予約入替');
      staging.stage(r2, { vehicle_id: String(r1.vehicle_id) }, '予約入替');
      setMode('none');
      setSelectedReservation(null);
      return;
    }

    const v1 = vehicles.find(v => String(v.id) === String(r1.vehicle_id));
    const v2 = vehicles.find(v => String(v.id) === String(r2.vehicle_id));
    const code1 = v1?.vehicle_code || r1.vehicle_id;
    const code2 = v2?.vehicle_code || r2.vehicle_id;

    const entry1 = buildMemoEntry('予約入替', `${code1} → ${code2}（${r2.customer_name || '予約'}と入替）`);
    const entry2 = buildMemoEntry('予約入替', `${code2} → ${code1}（${r1.customer_name || '予約'}と入替）`);
    const r1New = { ...r1, vehicle_id: r2.vehicle_id, memo: appendMemoEntry(r1.memo, entry1) };
    const r2New = { ...r2, vehicle_id: r1.vehicle_id, memo: appendMemoEntry(r2.memo, entry2) };

    beginOptimistic([r1New, r2New]);
    setMode('none');
    setSelectedReservation(null);

    scheduleUndoDismiss({
      label: '入替を元に戻す',
      execute: async () => {
        beginOptimistic([r1, r2]);
        try {
          const undone = await Promise.all([
            saveDispatchReservation(toScheduleOnlyPayload(r1)),
            saveDispatchReservation(toScheduleOnlyPayload(r2)),
          ]);
          settleOptimistic([r1.id], undone[0]);
          settleOptimistic([r2.id], undone[1]);
        } catch (e) {
          rollbackOptimistic([r1New, r2New]);
          throw e;
        }
      },
    });

    try {
      const saved = await Promise.all([saveScheduleChange(r1New, entry1), saveScheduleChange(r2New, entry2)]);
      settleOptimistic([r1New.id], saved[0]);
      settleOptimistic([r2New.id], saved[1]);
      onToast?.('入替が完了しました', 'success');
    } catch (e) {
      rollbackOptimistic([r1, r2]);
      console.error('[useMoveSwap] swap failed, reverted:', e);
      onToast?.('入替に失敗しました', 'error');
    }
  }, [selectedReservation, cancel, scheduleUndoDismiss, onToast, vehicles, staging]);

  // ========== 車両スワップ ==========

  /** 予約を入替対象 / 除外に分類 */
  const classifyReservations = useCallback((vehicleId: string): {
    targets: Reservation[];
    warnings: Reservation[];
    excluded: Reservation[];
  } => {
    const now = new Date();
    const targets: Reservation[] = [];
    const warnings: Reservation[] = [];
    const excluded: Reservation[] = [];

    for (const r of reservations) {
      if (String(r.vehicle_id) !== vehicleId) continue;
      const start = new Date(r.start_date);
      const end = new Date(r.end_date);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) continue;

      // 過去の予約（返却日時が現在より前）→ 除外
      if (end < now) {
        excluded.push(r);
        continue;
      }

      // 現在進行中（貸出日時 < 現在 < 返却日時）で in_use or done → 除外
      if (start < now && end > now) {
        const statusCode = String(r.status);
        if (statusCode === 'in_use' || statusCode === 'done' ||
            statusCode === '3' || statusCode === '4') {
          excluded.push(r);
          continue;
        }
      }

      // 入替対象
      targets.push(r);

      // 警告キーワードチェック
      const text = `${r.customer_name || ''} ${r.memo || ''} ${r.comment || ''} ${r.short_memo || ''}`;
      if (WARNING_KEYWORDS.some(kw => text.includes(kw))) {
        warnings.push(r);
      }
    }

    return { targets, warnings, excluded };
  }, [reservations]);

  /** 車両スワップ: ソース選択 */
  const selectVehicleSwapSource = useCallback((v: Vehicle) => {
    setVehicleSwapSource(v);
  }, []);

  /** 車両スワップ: ターゲット選択 → 確認情報を生成 */
  const selectVehicleSwapTarget = useCallback((v: Vehicle) => {
    if (!vehicleSwapSource) return;
    if (String(v.id) === String(vehicleSwapSource.id)) return;

    const classA = classifyReservations(String(vehicleSwapSource.id));
    const classB = classifyReservations(String(v.id));

    setVehicleSwapInfo({
      vehicleA: vehicleSwapSource,
      vehicleB: v,
      swapTargetsA: classA.targets,
      swapTargetsB: classB.targets,
      warningsA: classA.warnings,
      warningsB: classB.warnings,
      excludedA: classA.excluded,
      excludedB: classB.excluded,
    });
  }, [vehicleSwapSource, classifyReservations]);

  /** 車両スワップ: 実行 */
  const executeVehicleSwap = useCallback(async () => {
    if (!vehicleSwapInfo) return;
    const { vehicleA, vehicleB, swapTargetsA, swapTargetsB } = vehicleSwapInfo;

    // ステージングモード: 全対象予約の付け替えをローカルに積むだけ
    if (staging?.active) {
      for (const r of swapTargetsA) staging.stage(r, { vehicle_id: String(vehicleB.id) }, '車両入替');
      for (const r of swapTargetsB) staging.stage(r, { vehicle_id: String(vehicleA.id) }, '車両入替');
      setVehicleSwapInfo(null);
      setVehicleSwapSource(null);
      setMode('none');
      return;
    }

    // 楽観的更新: A の予約 → B の vehicle_id, B の予約 → A の vehicle_id（メモ追記）
    const originals = [...swapTargetsA, ...swapTargetsB].map(r => ({ ...r }));
    const codeA = vehicleA.vehicle_code || String(vehicleA.id);
    const codeB = vehicleB.vehicle_code || String(vehicleB.id);
    const entryAtoB = buildMemoEntry('車両入替', `${codeA} → ${codeB}`);
    const entryBtoA = buildMemoEntry('車両入替', `${codeB} → ${codeA}`);
    const updatedA = swapTargetsA.map(r => ({ ...r, vehicle_id: String(vehicleB.id), memo: appendMemoEntry(r.memo, entryAtoB) }));
    const updatedB = swapTargetsB.map(r => ({ ...r, vehicle_id: String(vehicleA.id), memo: appendMemoEntry(r.memo, entryBtoA) }));
    const allUpdated = [...updatedA, ...updatedB];
    /** 保存時にどのエントリを永続化するか（updatedA/B の順序と対応） */
    const entryFor = new Map<string, string>([
      ...updatedA.map(r => [String(r.id), entryAtoB] as [string, string]),
      ...updatedB.map(r => [String(r.id), entryBtoA] as [string, string]),
    ]);

    beginOptimistic(allUpdated);
    setVehicleSwapInfo(null);
    setVehicleSwapSource(null);
    setMode('none');

    const totalCount = allUpdated.length;
    onToast?.(`${totalCount}件の予約を入れ替え中...`, 'success');

    try {
      // 並列で保存（5件ずつバッチ。メモ永続化で1件あたり fetch+save の2リクエスト）
      const BATCH_SIZE = 5;
      for (let i = 0; i < allUpdated.length; i += BATCH_SIZE) {
        const batch = allUpdated.slice(i, i + BATCH_SIZE);
        const saved = await Promise.all(batch.map(r => saveScheduleChange(r, entryFor.get(String(r.id)))));
        // バッチごとに確定させる（全件終わるまで保護を持ち越さない）
        batch.forEach((r, idx) => settleOptimistic([r.id], saved[idx]));
      }
      onToast?.(`車両入替が完了しました（${totalCount}件）`, 'success');
    } catch (e) {
      // ロールバック
      rollbackOptimistic(originals);
      console.error('[useMoveSwap] vehicle swap failed, reverted:', e);
      onToast?.('車両入替に失敗しました', 'error');
    }
  }, [vehicleSwapInfo, onToast, staging]);

  const cancelVehicleSwap = useCallback(() => {
    setVehicleSwapInfo(null);
    setVehicleSwapSource(null);
    setMode('none');
  }, []);

  return {
    mode,
    selectedReservation,
    enterMove,
    enterSwap,
    enterVehicleSwap,
    startMove,
    startSwap,
    executeMove,
    executeSwap,
    cancel,
    undoAction,
    dismissUndo,
    vehicleSwapSource,
    vehicleSwapInfo,
    selectVehicleSwapSource,
    selectVehicleSwapTarget,
    executeVehicleSwap,
    cancelVehicleSwap,
  };
}
